import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { meetingsAPI } from '../services/api';
import { Logger } from '../utils/logger';
import { parseBackendDate } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface MeetingSlot {
  id: string;
  teacherName: string | null;
  className: string | null;
  startsAt: string;
  endsAt: string;
  location: string | null;
  status: string;
  bookedStudentName: string | null;
}

/**
 * Parent-teacher meetings: book an open slot for one of my children, see and
 * cancel my bookings, and put a booked meeting into my phone/Google calendar.
 * The server only ever shows slots for my own children's classes and only lets
 * me book for a child I am linked to — this screen adds no trust of its own.
 */
export function MeetingsScreen() {
  const navigation = useNavigation<any>();
  const { students, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [available, setAvailable] = useState<MeetingSlot[]>([]);
  const [bookings, setBookings] = useState<MeetingSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [availableRes, bookingsRes] = await Promise.all([
        meetingsAPI.getAvailable(),
        meetingsAPI.getMyBookings(),
      ]);
      setAvailable(availableRes.data ?? []);
      setBookings(bookingsRes.data ?? []);
    } catch (error) {
      Logger.warn('Meetings', 'Could not load meeting slots', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const notify = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      // RN-web's Alert is a no-op; the browser dialog is the honest fallback.
      (globalThis as any).alert?.(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const book = async (slot: MeetingSlot) => {
    const student = selectedStudent ?? students[0];
    if (!student) {
      notify('No student selected', 'Pick a student from the home screen first.');
      return;
    }

    setBusySlot(slot.id);
    try {
      await meetingsAPI.book(slot.id, student.id);
      notify('Meeting booked',
        `${formatWhen(slot)} with ${slot.teacherName ?? 'the teacher'} about ${student.name}. ` +
        'A confirmation with a calendar invite is on its way to your email.');
      await load();
    } catch (error: any) {
      notify('Could not book',
        error?.response?.data?.message ?? 'The slot may have just been taken. Pull to refresh.');
    } finally {
      setBusySlot(null);
    }
  };

  const cancel = async (slot: MeetingSlot) => {
    setBusySlot(slot.id);
    try {
      await meetingsAPI.cancelBooking(slot.id);
      notify('Booking cancelled', 'The slot is open for other parents again.');
      await load();
    } catch {
      notify('Could not cancel', 'Please try again.');
    } finally {
      setBusySlot(null);
    }
  };

  const addToCalendar = async (slot: MeetingSlot) => {
    setBusySlot(slot.id);
    try {
      const { data } = await meetingsAPI.getCalendar(slot.id);

      if (Platform.OS === 'web') {
        // Offer the .ics as a download AND open the Google Calendar template -
        // whichever calendar the parent lives in, one of the two lands.
        const doc: any = (globalThis as any).document;
        if (doc) {
          const BlobCtor = (globalThis as any).Blob;
          const blob = new BlobCtor([data.icsContent], { type: 'text/calendar' });
          const link = doc.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = data.fileName;
          link.click();
          URL.revokeObjectURL(link.href);
        }
        (globalThis as any).open?.(data.googleCalendarUrl, '_blank');
      } else {
        await Linking.openURL(data.googleCalendarUrl);
      }
    } catch (error) {
      Logger.warn('Meetings', 'Could not open the calendar link', error);
      notify('Calendar', 'The calendar invite is also attached to your confirmation email.');
    } finally {
      setBusySlot(null);
    }
  };

  const formatWhen = (slot: MeetingSlot) => {
    // parseBackendDate, not new Date: slot times carry no timezone marker, so a
    // plain parse offered parents a meeting window shifted by their UTC offset
    // (see dateUtils).
    const start = parseBackendDate(slot.startsAt);
    const end = parseBackendDate(slot.endsAt);
    if (!start || !end) return '';
    return `${start.toLocaleDateString(undefined, {
      weekday: 'short', day: 'numeric', month: 'short',
    })}, ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}–${
      end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const renderSlot = (slot: MeetingSlot, booked: boolean) => (
    <View key={slot.id} style={[styles.card, booked && styles.bookedCard]}>
      <Text style={styles.when}>{formatWhen(slot)}</Text>
      <Text style={styles.meta}>
        {[slot.teacherName, slot.className, slot.location].filter(Boolean).join(' • ') ||
          'Teacher slot'}
      </Text>
      {booked && slot.bookedStudentName && (
        <Text style={styles.meta}>About {slot.bookedStudentName}</Text>
      )}

      <View style={styles.actions}>
        {booked ? (
          <>
            <TouchableOpacity style={styles.secondaryButton}
              disabled={busySlot === slot.id}
              onPress={() => addToCalendar(slot)}>
              <Text style={styles.secondaryButtonText}>Add to calendar</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.dangerButton}
              disabled={busySlot === slot.id}
              onPress={() => cancel(slot)}>
              <Text style={styles.dangerButtonText}>Cancel booking</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity style={styles.primaryButton}
            disabled={busySlot === slot.id}
            onPress={() => book(slot)}>
            <Text style={styles.primaryButtonText}>
              {busySlot === slot.id ? 'Booking…' : 'Book this slot'}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <View style={styles.headerRow}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Text style={styles.back}>‹ Back</Text>
        </TouchableOpacity>
        <Text style={styles.header}>Parent-Teacher Meetings</Text>
      </View>

      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 40 }} />
      ) : (
        <>
          <Text style={styles.section}>My bookings</Text>
          {bookings.length === 0 && (
            <Text style={styles.empty}>No meetings booked yet.</Text>
          )}
          {bookings.map(slot => renderSlot(slot, true))}

          <Text style={styles.section}>Available slots</Text>
          {available.length === 0 && (
            <Text style={styles.empty}>
              No open slots right now — you will get a notification when a
              teacher opens new ones.
            </Text>
          )}
          {available.map(slot => renderSlot(slot, false))}
        </>
      )}
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.background },
  content: { padding: 16, paddingBottom: 40, maxWidth: 800, width: '100%', alignSelf: 'center' },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  back: { fontSize: 16, color: colors.primary, fontWeight: '600' },
  header: { fontSize: 20, fontWeight: '800', color: colors.textPrimary },
  section: { fontSize: 14, fontWeight: '700', color: colors.textSecondary, marginTop: 16, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  empty: { fontSize: 13, color: colors.textMuted, marginBottom: 8 },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 10, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 } },
  bookedCard: { borderLeftWidth: 4, borderLeftColor: '#16A34A' },
  when: { fontSize: 15, fontWeight: '700', color: colors.textPrimary, marginBottom: 4 },
  meta: { fontSize: 13, color: colors.textSecondary, marginBottom: 2 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 10, flexWrap: 'wrap' },
  primaryButton: { backgroundColor: colors.primary, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 },
  primaryButtonText: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  secondaryButton: { backgroundColor: colors.primaryLight, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 },
  secondaryButtonText: { color: colors.primary, fontWeight: '700', fontSize: 13 },
  dangerButton: { backgroundColor: '#FEF2F2', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 },
  dangerButtonText: { color: '#DC2626', fontWeight: '700', fontSize: 13 },
});
